import { useState, useEffect, useRef } from 'react';
import { adminReferralApi } from '@/api';
import { Flex } from '@/ui';
import { Button } from 'antd';
import { useNavigate } from 'react-router';

export function useDebounceSearch({ type }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);

  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => {
        fn.apply(this, args);
      }, delay);
    };
  }

  const fetchOptions = async (searchText) => {
    if (!searchText.trim()) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const result = await adminReferralApi.findReferralByCode({
        ...(type === 'assistant' && { referralCodeRecommender: searchText }),
        ...(type === 'agent' && { referralCodeAgent: searchText }),
        take: 10,
        skip: skip
      });

      const filteredInvestors = result?.data?.investors.map((investor) => ({
        value: investor.referralCodeRecommender,
        label: (
          <Flex justify='space-between'>
            <span>
              {`${investor.firstName} ${investor.lastName} (${type === 'assistant' ? investor.referralCodeRecommender : ''}${type === 'agent' ? investor.referralCodeAgent : ''})`}
            </span>
            <Button size='small' type='link' onClick={() => navigate(`/investor/${investor.id}`)}>
              Open profile
            </Button>
          </Flex>
        )
      }));

      setOptions((prevState) => [...prevState, ...filteredInvestors]);
      setTotal(result?.data?.total);
      setSkip((prevState) => prevState + 10);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchRef = useRef();

  useEffect(() => {
    debouncedFetchRef.current = debounce((text) => {
      setSkip(0);
      fetchOptions(text);
    }, 700);
  }, []);

  const loadMoreResults = (e, text) => {
    if (e.target.scrollTop + e.target.offsetHeight >= e.target.scrollHeight - 1 && total > skip) {
      fetchOptions(text);
    }
  };

  const handleSearch = (text) => {
    if (!text.trim()) {
      setOptions([]);
    }
    setSearchTerm(text);
    debouncedFetchRef.current?.(text);
  };

  const clearResults = () => {
    setOptions([]);
    setSearchTerm('');
  };

  return {
    options,
    searchTerm,
    setSearchTerm,
    loading,
    handleSearch,
    clearResults,
    fetchOptions,
    loadMoreResults
  };
}
